import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { timeout } from 'rxjs/operators';

import { IObserverFrame, IObserverTimeline } from 'app/shared/model/FDEObserverService/observer.model';
import { ObserverService } from 'app/logs/observer/observer.service';

const POLL_INTERVAL_MS = 2000;
const TICK_INTERVAL_MS = 250;
const DEFAULT_STEP_MS = 2000;
const MIN_STEP_MS = 200;
const MAX_STEP_MS = 4000;
const STEP_SAMPLE = 20;
const MAX_CATCHUP_FACTOR = 8;
const SNAP_BEHIND = 40;
const PREFETCH_BACK = 30;
const PREFETCH_AHEAD = 30;
const LIVE_PREFETCH_BACK = 4;
const BUNDLE_LIMIT = 300;
const MAX_CACHED_FRAMES = 900;
const SCRUB_SETTLE_MS = 120;
const REQUEST_TIMEOUT_MS = 15000;
const MAX_FRAMES = 20000;

@Component({
    selector: 'app-observer',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './observer.component.html',
    styleUrls: ['./observer.component.css']})
export class ObserverComponent implements OnInit, OnChanges, OnDestroy {
    @Input() runner: string;
    @Input() account: string;
    @Input() active = true;

    frames: IObserverFrame[] = [];
    index = 0;
    live = true;
    playing = false;
    executionId: string = null;
    error: string = null;

    private cursor: string = null;
    private objectUrls = new Map<number, string>();
    private unavailable = new Set<number>();
    private generation = 0;
    private bundleInFlight = false;
    private displayedSeq: number = null;
    private inFlight = false;
    private stepDueAt = 0;
    private pollHandle;
    private tickHandle;
    private scrubHandle;

    constructor(protected observerService: ObserverService) {}

    ngOnInit() {
        this.pollHandle = setInterval(() => this.tick(), POLL_INTERVAL_MS);
        this.tickHandle = setInterval(() => this.frameTick(), TICK_INTERVAL_MS);
        this.poll();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['runner'] || changes['account']) {
            this.reset();
        }
        this.poll();
    }

    ngOnDestroy() {
        clearInterval(this.pollHandle);
        clearInterval(this.tickHandle);
        clearTimeout(this.scrubHandle);
        this.playing = false;
        this.generation = this.generation + 1;
        this.releaseAll();
    }

    hasAccount(): boolean {
        return !!this.account;
    }

    hasFrames(): boolean {
        return this.frames.length > 0;
    }

    current(): IObserverFrame {
        return this.frames[this.index];
    }

    displayedUrl(): string {
        return this.displayedSeq === null ? null : this.objectUrls.get(this.displayedSeq);
    }

    isStale(): boolean {
        const frame = this.current();
        return !!frame && frame.seq != null && frame.seq !== this.displayedSeq;
    }

    behind(): number {
        return Math.max(this.frames.length - 1 - this.index, 0);
    }

    onSliderChange() {
        this.live = false;
        this.playing = false;
        clearTimeout(this.scrubHandle);
        this.scrubHandle = setTimeout(() => this.ensureLoaded(), SCRUB_SETTLE_MS);
    }

    goLive() {
        this.live = true;
        this.playing = false;
        this.index = Math.max(this.frames.length - 1, 0);
        this.stepDueAt = Date.now();
        this.ensureLoaded();
    }

    step(delta: number) {
        this.live = false;
        this.playing = false;
        this.index = Math.min(Math.max(this.index + delta, 0), Math.max(this.frames.length - 1, 0));
        this.ensureLoaded();
    }

    togglePlayback() {
        if (this.playing) {
            this.playing = false;
            return;
        }
        this.live = false;
        this.playing = true;
        this.stepDueAt = Date.now();
    }

    elapsed(frame: IObserverFrame): string {
        if (!frame || !frame.ts || !this.frames.length || !this.frames[0].ts) {
            return '';
        }
        const seconds = Math.round((frame.ts - this.frames[0].ts) / 1000);
        const minutes = Math.floor(seconds / 60);
        return minutes + 'm ' + (seconds % 60) + 's';
    }

    clock(frame: IObserverFrame): string {
        if (!frame || !frame.ts) {
            return '';
        }
        const date = new Date(frame.ts);
        return this.pad(date.getHours()) + ':' + this.pad(date.getMinutes()) + ':' + this.pad(date.getSeconds());
    }

    private frameTick() {
        if (!this.active || document.hidden || !this.hasFrames()) {
            return;
        }
        if (this.live) {
            this.followLive();
            return;
        }
        if (this.playing) {
            this.playForward();
        }
    }

    private followLive() {
        const behind = this.behind();
        if (behind === 0) {
            return;
        }
        if (behind >= SNAP_BEHIND) {
            this.index = this.frames.length - 1;
            this.stepDueAt = Date.now() + this.stepMs();
            this.ensureLoaded();
            return;
        }
        this.advanceWhenDue(Math.min(behind, MAX_CATCHUP_FACTOR));
    }

    private playForward() {
        if (this.index >= this.frames.length - 1) {
            this.playing = false;
            return;
        }
        this.advanceWhenDue(1);
    }

    private advanceWhenDue(speed: number) {
        const now = Date.now();
        if (now < this.stepDueAt) {
            return;
        }
        const next = this.frames[this.index + 1];
        if (!next) {
            return;
        }
        if (next.seq != null && !this.objectUrls.has(next.seq) && !this.unavailable.has(next.seq)) {
            this.ensureLoaded();
            return;
        }
        this.index = this.index + 1;
        this.stepDueAt = now + Math.max(this.stepMs() / speed, MIN_STEP_MS);
        this.ensureLoaded();
    }

    private stepMs(): number {
        const window = this.frames.slice(-STEP_SAMPLE);
        const deltas: number[] = [];
        for (let i = 1; i < window.length; i++) {
            const previous = window[i - 1].ts;
            const next = window[i].ts;
            if (previous && next && next > previous) {
                deltas.push(next - previous);
            }
        }
        if (deltas.length === 0) {
            return DEFAULT_STEP_MS;
        }
        deltas.sort((left, right) => left - right);
        const median = deltas[Math.floor(deltas.length / 2)];
        return Math.min(Math.max(median, MIN_STEP_MS), MAX_STEP_MS);
    }

    private reset() {
        this.generation = this.generation + 1;
        this.frames = [];
        this.index = 0;
        this.cursor = null;
        this.executionId = null;
        this.error = null;
        this.live = true;
        this.playing = false;
        this.stepDueAt = 0;
        this.releaseAll();
        this.displayedSeq = null;
        this.inFlight = false;
    }

    private ensureLoaded() {
        this.refreshDisplayed();
        const range = this.missingRange();
        if (range) {
            this.loadBundle(range.from, range.count);
        }
        this.evict();
    }

    private refreshDisplayed() {
        const frame = this.current();
        if (frame && frame.seq != null && this.objectUrls.has(frame.seq)) {
            this.displayedSeq = frame.seq;
        }
    }

    private missingRange(): { from: number; count: number } {
        const back = this.live ? LIVE_PREFETCH_BACK : PREFETCH_BACK;
        const first = Math.max(this.index - back, 0);
        const last = Math.min(this.index + PREFETCH_AHEAD, this.frames.length - 1);
        let from: number = null;
        let to: number = null;
        for (let i = first; i <= last; i++) {
            const frame = this.frames[i];
            if (!frame || frame.seq == null || this.objectUrls.has(frame.seq) || this.unavailable.has(frame.seq)) {
                continue;
            }
            if (from === null) {
                from = frame.seq;
            }
            to = frame.seq;
        }
        if (from === null) {
            return null;
        }
        return { from, count: Math.min(to - from + 1, BUNDLE_LIMIT) };
    }

    private loadBundle(from: number, count: number) {
        if (this.bundleInFlight || !this.executionId) {
            return;
        }
        this.bundleInFlight = true;
        const generation = this.generation;
        this.observerService.bundle(this.runner, this.account, this.executionId, from, count)
            .pipe(timeout(REQUEST_TIMEOUT_MS))
            .subscribe(
                (buffer: ArrayBuffer) => {
                    this.bundleInFlight = false;
                    if (generation !== this.generation) {
                        return;
                    }
                    this.absorbBundle(buffer);
                    this.markUndelivered(from, count);
                    this.refreshDisplayed();
                    this.ensureLoaded();
                },
                () => (this.bundleInFlight = false)
            );
    }

    private markUndelivered(from: number, count: number) {
        for (let seq = from; seq < from + count; seq++) {
            if (!this.objectUrls.has(seq)) {
                this.unavailable.add(seq);
            }
        }
    }

    private absorbBundle(buffer: ArrayBuffer) {
        const view = new DataView(buffer);
        let offset = 0;
        while (offset + 8 <= view.byteLength) {
            const seq = view.getInt32(offset);
            const length = view.getInt32(offset + 4);
            offset += 8;
            if (length < 0 || offset + length > view.byteLength) {
                break;
            }
            if (!this.objectUrls.has(seq)) {
                const bytes = new Uint8Array(buffer, offset, length);
                this.objectUrls.set(seq, URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' })));
                this.unavailable.delete(seq);
            }
            offset += length;
        }
    }

    private evict() {
        if (this.objectUrls.size <= MAX_CACHED_FRAMES) {
            return;
        }
        const current = this.frames[this.index];
        const anchor = current && current.seq != null ? current.seq : 0;
        const byDistance = Array.from(this.objectUrls.keys())
            .sort((left, right) => Math.abs(right - anchor) - Math.abs(left - anchor));
        let removed = 0;
        const excess = this.objectUrls.size - MAX_CACHED_FRAMES;
        for (const seq of byDistance) {
            if (removed >= excess) {
                break;
            }
            if (seq === this.displayedSeq) {
                continue;
            }
            URL.revokeObjectURL(this.objectUrls.get(seq));
            this.objectUrls.delete(seq);
            removed++;
        }
    }

    private releaseAll() {
        this.objectUrls.forEach(url => URL.revokeObjectURL(url));
        this.objectUrls.clear();
        this.unavailable.clear();
        this.bundleInFlight = false;
    }

    private tick() {
        if (!this.active || document.hidden || !this.hasAccount()) {
            return;
        }
        this.poll();
    }

    private poll() {
        if (!this.active || this.inFlight || !this.runner || !this.hasAccount()) {
            return;
        }
        this.inFlight = true;
        const generation = this.generation;
        this.observerService.timeline(this.runner, this.account, this.cursor)
            .pipe(timeout(REQUEST_TIMEOUT_MS))
            .subscribe(
                (res: HttpResponse<IObserverTimeline>) => {
                    if (generation !== this.generation) {
                        return;
                    }
                    this.inFlight = false;
                    this.error = null;
                    this.absorb(res.body);
                },
                () => {
                    if (generation !== this.generation) {
                        return;
                    }
                    this.inFlight = false;
                    this.error = 'Observer stream is unreachable, retrying';
                }
            );
    }

    private absorb(timeline: IObserverTimeline) {
        if (!timeline) {
            return;
        }
        if (this.executionId && timeline.executionId !== this.executionId) {
            const started = timeline.executionId;
            this.reset();
            this.executionId = started;
            return;
        }
        this.executionId = timeline.executionId;
        this.cursor = timeline.cursor || this.cursor;
        const known = new Set(this.frames.map(frame => frame.seq));
        const incoming = (timeline.frames || []).filter(frame => frame.seq != null && !known.has(frame.seq));
        if (incoming.length === 0) {
            return;
        }
        const wasEmpty = this.frames.length === 0;
        this.frames = this.frames.concat(incoming);
        if (this.frames.length > MAX_FRAMES) {
            this.frames = this.frames.slice(this.frames.length - MAX_FRAMES);
            this.index = Math.min(this.index, this.frames.length - 1);
        }
        if (this.live && wasEmpty) {
            this.index = this.frames.length - 1;
            this.stepDueAt = Date.now();
        }
        this.ensureLoaded();
    }

    private pad(value: number): string {
        return value < 10 ? '0' + value : String(value);
    }
}
